import { useState, useEffect } from 'react'
import { Shield, Palette, Coins, TrendingUp, Plus } from 'lucide-react'
import { blockchainService, MintingOptions } from '@/services/blockchainService'
import { contentApi } from '@/services/api'
import toast from 'react-hot-toast'

const NFTs: React.FC = () => {
  const [nfts, setNfts] = useState<any[]>([])
  const [contentItems, setContentItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showMintModal, setShowMintModal] = useState(false)
  const [selectedContent, setSelectedContent] = useState<any>(null)
  const [mintingOptions, setMintingOptions] = useState<MintingOptions>({
    name: '',
    description: '',
    image: '',
    attributes: [],
    blockchain: 'ethereum',
    royaltyPercentage: 10
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load user's NFTs
      const nftsResponse = await blockchainService.getUserNFTs()
      setNfts(nftsResponse.data || [])

      // Load user's content
      const contentResponse = await contentApi.getContent()
      setContentItems(contentResponse.data?.data || [])
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMintNFT = async () => {
    if (!selectedContent) return

    try {
      await blockchainService.mintNFT(selectedContent.id, mintingOptions)
      toast.success('NFT minted successfully!')
      setShowMintModal(false)
      loadData() // Refresh data
    } catch (error: any) {
      toast.error(error.message || 'NFT minting failed')
    }
  }

  const getBlockchainIcon = (blockchain: string) => {
    switch (blockchain) {
      case 'ethereum':
        return '🔷'
      case 'polygon':
        return '🟣'
      case 'solana':
        return '🟡'
      default:
        return '🔗'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">NFT Collection</h1>
          <p className="text-gray-600 mt-1">Manage your digital assets</p>
        </div>
        <button
          onClick={() => setShowMintModal(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Mint NFT
        </button>
      </div>

      {/* NFT Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Palette className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total NFTs</p>
              <p className="text-2xl font-bold text-gray-900">{nfts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">$0.00</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Coins className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Royalties</p>
              <p className="text-2xl font-bold text-gray-900">$0.00</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Shield className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Protected</p>
              <p className="text-2xl font-bold text-gray-900">{nfts.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* NFT Grid */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Your NFTs</h3>
        {nfts.length === 0 ? (
          <div className="text-center py-8">
            <Palette className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">No NFTs yet. Mint your first NFT!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nfts.map((nft, index) => (
              <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="aspect-square bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
                  {nft.metadata?.image ? (
                    <img 
                      src={nft.metadata.image} 
                      alt={nft.metadata.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <Palette className="h-12 w-12 text-gray-400" />
                  )}
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900">{nft.metadata?.name || 'Untitled'}</h4>
                  <p className="text-sm text-gray-500">{nft.metadata?.description || 'No description'}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {getBlockchainIcon(nft.blockchain)} {blockchainService.getNetworkDisplayName(nft.blockchain)}
                    </span>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      #{nft.tokenId}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mint NFT Modal */}
      {showMintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Mint NFT</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Content
                </label>
                <select
                  value={selectedContent?.id || ''}
                  onChange={(e) => {
                    const content = contentItems.find(c => c.id === e.target.value)
                    setSelectedContent(content)
                    if (content) {
                      setMintingOptions(prev => ({
                        ...prev,
                        name: content.metadata?.title || content.originalFilename,
                        description: content.metadata?.description || '',
                        image: content.metadata?.image || ''
                      }))
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select content to mint</option>
                  {contentItems.map((content) => (
                    <option key={content.id} value={content.id}>
                      {content.metadata?.title || content.originalFilename}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NFT Name
                </label>
                <input
                  type="text"
                  value={mintingOptions.name}
                  onChange={(e) => setMintingOptions(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={mintingOptions.description}
                  onChange={(e) => setMintingOptions(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Blockchain
                </label>
                <select
                  value={mintingOptions.blockchain}
                  onChange={(e) => setMintingOptions(prev => ({ ...prev, blockchain: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {blockchainService.getSupportedBlockchains().map((blockchain) => (
                    <option key={blockchain} value={blockchain}>
                      {blockchainService.getNetworkDisplayName(blockchain)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Royalty Percentage
                </label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={mintingOptions.royaltyPercentage}
                  onChange={(e) => setMintingOptions(prev => ({ ...prev, royaltyPercentage: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowMintModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleMintNFT}
                disabled={!selectedContent}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mint NFT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NFTs 